import { format } from 'date-fns';
import { FC, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { RichInputContextProvider, RichMediaInput, RichMediaState, useRichInput } from '@bonusly/common-action-input';
import { useFetchRecipients } from '@bonusly/common-give-box';
import { RichTextInput, useRichText } from '@bonusly/common-rich-text-editor';
import {
	Box,
	Button,
	Checkbox,
	CheckboxGroup,
	Flex,
	FormControl,
	FormErrorMessage,
	FormHelperText,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	rem,
	Spacer,
	Stack,
	Text,
	useDisclosure,
	useToast,
} from '@bonusly/design-system';
import { GroupType, Recipient } from '@bonusly/recipient-entities';
import { useCurrentCompany } from '@bonusly/who-am-i';

import { FormLabel } from '../../form/form-label';
import { useCreateAnnouncement, useGetAnnouncementsCreatedByUser, useInvalidateAnnouncementsQueries } from '../hooks/use-announcements';
import { useGetEveryoneRecipient } from '../hooks/use-recipients';
import { endDateIsAfterStartDate, isFutureDate, isFutureOrToday } from '../lib/form-validations';
import { FormValues } from '../types/announcements';
import { createRecipientContent } from '../utils/content';
import { formatDateISOString } from '../utils/date';
import { ImageUploadField } from './image-upload-field';
import { LoadingAnnouncement } from './loading-announcement';
import { ManageAnnouncementsModal } from './manage-announcements-modal';
import { SelectIncentivesDropdown } from './select-incentives-dropdown';

export interface MakeAnnouncementModalProps {
	hideManageLink?: boolean;
	isOpen: boolean;
	onClose: VoidFunction;
	onSaveChanges?: (data: any) => void;
	initialDefaults?: {
		title?: string;
		description?: string;
		end_date?: string;
		start_date?: string;
		email_notification?: boolean;
		send_slack_message?: boolean;
		send_ms_teams_message?: boolean;
		claimable_award_id?: string;
		image?: File | undefined;
		recipients?: string[];
		reason_tokens?: any[];
	};
}

const MakeAnnouncementModal: FC<MakeAnnouncementModalProps> = ({ hideManageLink, isOpen, onClose, onSaveChanges, initialDefaults }) => {
	const isPreviewMode = !!onSaveChanges;
	const { t } = useTranslation('announcements');
	const { track } = useAnalytics();
	const toast = useToast();
	const invalidateQueries = useInvalidateAnnouncementsQueries();
	const manageModal = useDisclosure();
	const fetchRecipients = useFetchRecipients({
		search_users: false,
		include_self: true,
	});
	const company = useCurrentCompany();
	const { mutate: createAnnouncement, isLoading } = useCreateAnnouncement();
	const { data: userAnnouncements } = useGetAnnouncementsCreatedByUser();
	const { data: everyoneRecipients, isLoading: isLoadingRecipient } = useGetEveryoneRecipient();
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const msteamsMessageEnabled = company?.chat_integrations?.has_ms_teams_integration ?? false;
	const slackMessageEnabled = company?.chat_integrations?.has_slack_integration ?? false;

	const userHasAnnouncements = userAnnouncements && (userAnnouncements.posted.length > 0 || userAnnouncements.scheduled.length > 0);

	const today = format(new Date(), 'yyyy-MM-dd');
	// Form setup
	const methods = useForm<FormValues>({
		defaultValues: {
			description: '',
			email_notification: false,
			send_slack_message: false,
			send_ms_teams_message: false,
			title: '',
			recipients: [],
			start_date: today,
		},
		shouldFocusError: false,
	});
	const {
		clearErrors,
		register,
		handleSubmit,
		formState: { errors },
		reset,
		setValue,
		watch,
	} = methods;

	// reset form and error messages on load
	useEffect(() => {
		if (!isOpen) return;

		// Set initial values if provided, otherwise use defaults
		setValue('title', initialDefaults?.title ?? '');
		setValue('description', initialDefaults?.description ?? '');
		setValue('start_date', initialDefaults?.start_date ?? today);
		setValue('end_date', initialDefaults?.end_date ?? '');
		setValue('email_notification', initialDefaults?.email_notification ?? false);
		setValue('send_slack_message', initialDefaults?.send_slack_message ?? false);
		setValue('send_ms_teams_message', initialDefaults?.send_ms_teams_message ?? false);
		setValue('claimable_award_id', initialDefaults?.claimable_award_id ?? '');
		setValue('image', initialDefaults?.image ? [initialDefaults.image] : undefined);
		setValue('recipients', initialDefaults?.recipients ?? []);

		clearErrors();
		setErrorMessage(null);

		track(AnalyticsEvent.Announcements.MakeAnnouncementModalViewed);
	}, [clearErrors, isOpen, setValue, today, initialDefaults, track]);

	const onSubmit = (formValues: FormValues) => {
		if (errorMessage) setErrorMessage(null);

		const params = {
			...formValues,
			claimable_award_id: formValues.claimable_award_id || null,
			end_date: formatDateISOString(formValues.end_date),
			image: formValues.image ? formValues.image[0] : null,
			...(formValues.start_date && {
				start_date: formatDateISOString(formValues.start_date),
			}),
			reason_tokens: recipientData.recipients.map(({ id, dimension, displayName, username, userIds }) => ({
				context: 'recipient' as const,
				display_name: displayName,
				id: id,
				token: `@${username}`,
				type: (dimension as GroupType) || 'username',
				user_ids: userIds,
			})),
		};

		// When a handler is passed we override the default behavior
		if (onSaveChanges) {
			// Filter out empty files from image array and normalize date formats
			const filteredParams = {
				...params,
				image: formValues.image && formValues.image.length > 0 && formValues.image[0].size > 0 ? formValues.image[0] : null,
				// Normalize date formats to yyyy-MM-dd for form compatibility
				// Extract just the date part to avoid timezone issues
				start_date: params.start_date?.split('T')[0] || params.start_date,
				end_date: params.end_date?.split('T')[0] || params.end_date,
			};
			onSaveChanges(filteredParams);
			onClose();
			return;
		}

		track(AnalyticsEvent.Announcements.CreateAnnouncementRequestSubmitted);
		createAnnouncement(params, {
			onError: () => {
				setErrorMessage(t('modal.create_error_message'));
				track(AnalyticsEvent.Announcements.CreateAnnouncementFailure);
			},
			onSuccess: () => {
				reset();
				track(AnalyticsEvent.Announcements.CreateAnnouncementSuccess);
				recipientEditor?.commands.clearContent();
				descriptionEditor?.commands.clearContent();
				invalidateQueries();
				onClose();
				toast({
					title: t('modal.success_message'),
					status: 'success',
					position: 'bottom-right',
				});
			},
		});
	};

	const title = watch('title');
	const description = watch('description');
	const showManageLink = !hideManageLink && userHasAnnouncements;

	const { editor: recipientEditor, uniqueId } = useRichInput({
		fetchRecipients,
		modules: ['recipient'],
		options: {
			recipient: {
				matchWithoutTrigger: true,
			},
		},
		placeholder: t('modal.recipient_placeholder'),
	});

	const {
		editor: descriptionEditor,
		html: descriptionHtml,
		text: descriptionText,
	} = useRichText({
		content: initialDefaults?.description || '',
		placeholder: t('modal.description_placeholder'),
		editorProps: {
			attributes: {
				'data-testid': 'make-announcement-description',
			},
		},
	});

	// NOTE: since description is a rich text editor we need to manually register the field
	// and update the form value and trigger validation when it changes
	register('description', {
		validate: {
			value: () => {
				if (descriptionText.trim() === '') {
					return t('modal.description_required');
				}

				if (descriptionText.length > 250) {
					return t('modal.description_too_long');
				}

				return true;
			},
		},
	});

	useEffect(() => {
		if (description !== descriptionHtml) {
			setValue('description', descriptionHtml, {
				shouldValidate: !!description,
			});
		}
	}, [description, descriptionHtml, setValue]);

	useEffect(() => {
		if (isOpen && initialDefaults?.description && descriptionEditor) {
			descriptionEditor.commands.setContent(initialDefaults.description);
		}
	}, [isOpen, initialDefaults?.description, descriptionEditor]);

	// NOTE: since recipients is a rich input editor we need to manually register the field
	// for error handling and validation
	register('recipients', {
		required: t('modal.recipient_required'),
	});

	const recipients = watch('recipients');
	const recipientData = RichMediaState.parse(recipientEditor);

	useEffect(() => {
		if (isOpen && recipientEditor) {
			// If we have reason_tokens from initialDefaults, use those directly
			if (initialDefaults?.reason_tokens && initialDefaults.reason_tokens.length > 0) {
				const recipients = initialDefaults.reason_tokens
					.map((obj: any) => ({ ...obj, token: obj.token.split('@')[1] }))
					.map((token: any) => Recipient.fromReasonToken(token, everyoneRecipients || []))
					.filter((recipient): recipient is Recipient => recipient !== undefined);

				if (recipients.length > 0) {
					recipientEditor.commands.setContent(createRecipientContent(recipients));
				}
			} else if (everyoneRecipients && !initialDefaults?.recipients) {
				recipientEditor.commands.setContent(createRecipientContent(everyoneRecipients));
			}
		}
	}, [isOpen, everyoneRecipients, recipientEditor, initialDefaults?.recipients, initialDefaults?.reason_tokens]);

	useEffect(() => {
		const existingRecipientIds = recipients.join(',');
		const newRecipients = recipientData.recipients.map((recipient) => recipient.id);
		//  compare array contents
		if (existingRecipientIds !== newRecipients.join(',')) {
			setValue('recipients', newRecipients, {
				shouldValidate: true,
			});
		}
	}, [recipients, recipientData.recipients, setValue]);

	const getButtonText = () => {
		if (isPreviewMode) return t('modal.save_button');
		const startDate = watch('start_date');
		if (startDate && isFutureDate(startDate)) return t('modal.schedule_button');
		return t('modal.submit_button');
	};

	const handleManageClick = () => {
		track(AnalyticsEvent.Announcements.ManageAnnouncementsButtonClicked);
		onClose();
		manageModal.onOpen();
	};

	return (
		<>
			<Modal
				autoFocus={false}
				isOpen={isOpen}
				onClose={() => {
					// NOTE: clearing description and recipient field on close
					// to prevent it from showing up the next time the modal is opened
					recipientEditor?.commands.setContent('');
					descriptionEditor?.commands.setContent('');
					track(AnalyticsEvent.Announcements.MakeAnnouncementModalClosed);
					onClose();
				}}
				scrollBehavior="inside"
				size={{ base: 'full', md: '2xl' }}
			>
				<ModalOverlay />
				<FormProvider {...methods}>
					<ModalContent as="form" onSubmit={handleSubmit(onSubmit)} borderRadius={{ base: 0, md: 'xl' }}>
						<ModalHeader borderBottomWidth={rem(1)} borderColor="border.soft" borderStyle="solid" color="text.default">
							{t('modal.create_header')}
						</ModalHeader>
						<ModalCloseButton data-testid="close-button" size="lg" borderRadius="full" />
						{isLoadingRecipient ? (
							<LoadingAnnouncement />
						) : (
							<ModalBody px={5} pt={5} pb={0}>
								<Stack spacing={5}>
									<FormControl isInvalid={!!errors.recipients}>
										<FormLabel fontSize={50}>{t('modal.recipient_label')}</FormLabel>

										<RichInputContextProvider value={{ parentComponent: 'announcement-modal' }}>
											<RichMediaInput
												borderColor={errors?.recipients ? 'red.500' : 'border.input'}
												borderWidth={errors?.recipients ? rem(2) : rem(1)}
												borderRadius="md"
												editor={recipientEditor}
												fontSize={50}
												px={2}
												py={0}
												uniqueId={uniqueId}
											/>
										</RichInputContextProvider>

										<FormErrorMessage>{errors?.recipients?.message}</FormErrorMessage>
										<FormHelperText>{t('modal.recipient_helper')}</FormHelperText>
									</FormControl>

									<FormControl isInvalid={!!errors.title}>
										<FormLabel fontSize={50}>{t('modal.title_label')}</FormLabel>
										<Input
											placeholder={t('modal.title_placeholder')}
											maxLength={50}
											borderColor="border.input"
											fontSize={50}
											_hover={{ borderColor: 'border.input' }}
											_focus={{
												borderColor: 'border.input',
												boxShadow: 'none',
											}}
											{...register('title', {
												required: t('modal.title_required'),
											})}
										/>
										<FormErrorMessage>{errors?.title?.message}</FormErrorMessage>
										<FormHelperText>{t('modal.title_helper', { count: title.length })}</FormHelperText>
									</FormControl>

									<FormControl isInvalid={!!errors.description}>
										<FormLabel fontSize={50}>{t('modal.description_label')}</FormLabel>
										<Box borderColor={errors?.description ? 'red.500' : 'border.input'} borderWidth={errors?.description ? rem(2) : rem(1)} borderRadius="md">
											<RichTextInput editor={descriptionEditor} fontSize={50} border="none" />
										</Box>
										<FormErrorMessage>{errors?.description?.message}</FormErrorMessage>
										<FormHelperText>
											{t('modal.description_helper', {
												count: descriptionText.length,
											})}
										</FormHelperText>
									</FormControl>

									<FormControl isInvalid={!!errors.start_date}>
										<FormLabel fontSize={50}>{t('modal.start_date_label')}</FormLabel>
										<Input
											data-testid="start-date-input"
											type="date"
											borderColor="border.input"
											fontSize={50}
											_hover={{ borderColor: 'border.input' }}
											_focus={{
												borderColor: 'border.input',
												boxShadow: 'none',
											}}
											{...register('start_date', {
												required: t('modal.start_date_required'),
												validate: {
													futureOrToday: (value: string) => {
														if (!value) return true;
														if (isFutureOrToday(value)) return true;
														return t('modal.start_date_future');
													},
												},
											})}
										/>
										<FormErrorMessage>{errors?.start_date?.message}</FormErrorMessage>
										<FormHelperText>{t('modal.start_date_helper')}</FormHelperText>
									</FormControl>

									<FormControl isInvalid={!!errors.end_date}>
										<FormLabel fontSize={50}>{t('modal.end_date_label')}</FormLabel>
										<Input
											data-testid="end-date-input"
											type="date"
											borderColor="border.input"
											fontSize={50}
											_hover={{ borderColor: 'border.input' }}
											_focus={{
												borderColor: 'border.input',
												boxShadow: 'none',
											}}
											{...register('end_date', {
												required: t('modal.end_date_required'),
												validate: {
													futureDate: (value: string) => {
														if (isFutureDate(value)) return true;
														return t('modal.end_date_future');
													},
													afterStart: (value: string, formValues: FormValues) => {
														if (endDateIsAfterStartDate(value, formValues)) {
															return true;
														}
														return t('modal.end_date_after_start');
													},
												},
											})}
										/>
										<FormErrorMessage>{errors?.end_date?.message}</FormErrorMessage>
										<FormHelperText>{t('modal.end_date_helper')}</FormHelperText>
									</FormControl>

									<SelectIncentivesDropdown register={register} recipients={recipientData.recipients} />

									<ImageUploadField />

									<Box>
										<FormLabel fontSize={50} isOptional>
											{t('modal.notification_label')}
										</FormLabel>
										<FormControl>
											<CheckboxGroup>
												<Checkbox {...register('email_notification')}>
													<Text display="inline-block" pt={1.5} fontSize={50} fontWeight="normal" lineHeight={5}>
														{t('modal.email_notification_label')}
													</Text>
												</Checkbox>
												<Checkbox mt={2} display={slackMessageEnabled ? 'block' : 'none'} {...register('send_slack_message')}>
													<Text display="inline-block" fontSize={50} fontWeight="normal" lineHeight={5}>
														{t('modal.send_slack_message_label')}
													</Text>
												</Checkbox>
												<Checkbox mt={2} display={msteamsMessageEnabled ? 'block' : 'none'} {...register('send_ms_teams_message')}>
													<Text display="inline-block" fontSize={50} fontWeight="normal" lineHeight={5}>
														{t('modal.send_ms_teams_message_label')}
													</Text>
												</Checkbox>
											</CheckboxGroup>
										</FormControl>
									</Box>
									<Spacer h={5} />
								</Stack>
							</ModalBody>
						)}

						<ModalFooter flexDirection="column" borderTopColor="border.default" borderTopStyle="solid" borderTopWidth="1px">
							<Flex justify={showManageLink ? 'space-between' : 'flex-end'} w="100%">
								{showManageLink && (
									<Button variant="link" onClick={handleManageClick}>
										{t('modal.manage_button')}
									</Button>
								)}
								<Button colorScheme="primary" data-testid="save-button" isLoading={isLoading} type="submit">
									{getButtonText()}
								</Button>
							</Flex>
							{errorMessage && (
								<Text color="error.text" fontSize={50} mt={4} textAlign="right" w="100%">
									{errorMessage}
								</Text>
							)}
						</ModalFooter>
					</ModalContent>
				</FormProvider>
			</Modal>
			<ManageAnnouncementsModal isOpen={manageModal.isOpen} onClose={manageModal.onClose} userAnnouncements={userAnnouncements} />
		</>
	);
};

MakeAnnouncementModal.displayName = 'Make Announcement Modal';

export { MakeAnnouncementModal };
